/* Recipe Box (Shopify-safe, scoped)
   - Mounts into:  #recipe-box-root #recipe-box-app
   - Expects templates in the section HTML (tpl-page, tpl-tile, tpl-detail, tpl-add, tpl-plan, tpl-list)
*/

(() => {
  "use strict";

  const ROOT_ID = "recipe-box-root";
  const HOST_ID = "recipe-box-app";

  const STORAGE = {
    myIds: "recipeBox_myIds_v1",
    ratings: "recipeBox_ratings_v1",
    servings: "recipeBox_servings_v1",
    custom: "recipeBox_customRecipes_v1",
    plan: "recipeBox_mealPlan_v1",
    listChecks: "recipeBox_listChecks_v1",
  };

let DATA_URL = window.RECIPE_BOX_DATA_URL || "recipes.json";
if (DATA_URL.startsWith("//")) DATA_URL = window.location.protocol + DATA_URL;


// If you provide a Storefront token, we’ll load recipes from Metaobjects instead of recipes.json
const STOREFRONT_TOKEN = window.RECIPE_BOX_STOREFRONT_TOKEN || "";
const STOREFRONT_API_VERSION = window.RECIPE_BOX_STOREFRONT_API_VERSION || "2025-07";
const USE_METAOBJECTS = !!STOREFRONT_TOKEN;


  // -------- helpers --------
  const byId = (id, root = document) => root.getElementById(id);
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const getRoot = () => document.getElementById(ROOT_ID);
  const getHost = () => {
    const root = getRoot();
    return root ? qs(`#${HOST_ID}`, root) : null;
  };

  function safeJsonParse(str, fallback) {
    try { return JSON.parse(str); } catch { return fallback; }
  }

  function loadLS(key, fallback) {
    return safeJsonParse(localStorage.getItem(key) || "", fallback);
  }

  function saveLS(key, val) {
    localStorage.setItem(key, JSON.stringify(val));
  }

  function cloneTpl(id) {
    const root = getRoot();
    const tpl = root ? qs(`#${id}`, root) : null;
    if (!tpl) throw new Error(`Missing template #${id} in section HTML`);
    return document.importNode(tpl.content, true);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;"
    }[c]));
  }

  function setActiveTab() {
    const root = getRoot();
    if (!root) return;

    const route = (location.hash || "#/all").split("?")[0];

    qsa(".tab", root).forEach(btn => {
      const r = btn.getAttribute("data-route") || "";
      const active = (r === route);

      btn.classList.toggle("is-active", active);
      if (active) btn.setAttribute("aria-current", "page");
      else btn.removeAttribute("aria-current");
    });
  }


  // -------- helper functions for Storefront API + metaobject parsing --------
  function parseRoute() {
    const h = location.hash || "#/all";
    const [path] = h.split("?");
    const parts = path.replace(/^#\//, "").split("/");
    const page = parts[0] || "all";
    const id = parts[1] || "";
    return { page, id };
  }

  function showMessageCard(html) {
    const host = getHost();
    if (!host) return;
    host.innerHTML = `<div class="card" style="margin:16px 0;">${html}</div>`;
  }

  async function storefrontGraphQL(query, variables = {}) {
  const url = `${window.location.origin}/api/${STOREFRONT_API_VERSION}/graphql.json`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  const json = await res.json();
  if (!res.ok || json.errors) {
    console.error("Storefront GraphQL error:", json);
    throw new Error("Storefront API request failed. Check token + metaobject storefront access.");
  }
  return json.data;
}

function fieldMapFromMetaobject(mo) {
  const map = {};
  (mo.fields || []).forEach(f => {
    map[f.key] = f;
  });
  return map;
}

function parseListFieldValue(v) {
  // Storefront often returns list fields as JSON-stringified arrays
  // e.g. '["instant-pot","crockpot"]'
  if (!v) return [];
  if (Array.isArray(v)) return v.map(String);

  const s = String(v).trim();
  if (!s) return [];

  const asJson = safeJsonParse(s, null);
  if (Array.isArray(asJson)) return asJson.map(String);

  // fallback: allow pipe format if you ever store it that way
  return s.split("|").map(x => x.trim()).filter(Boolean);
}

function metaobjectToBaseRecipe(mo) {
  const fm = fieldMapFromMetaobject(mo);

  const title = (fm.title?.value || "").trim();
  const slug = (fm.slug?.value || mo.handle || "").trim();

  // Your UI expects:
  // id, title, image, method, minutes, tags[], ingredients[], instructions[], notes
  const servings = Number(fm.servings?.value || 4) || 4;

  const totalMin = Number(fm.total_time_min?.value || 0) || 0;

  const appliance = parseListFieldValue(fm.appliance?.value);
  const tags = parseListFieldValue(fm.tags?.value);
  const dietFlags = parseListFieldValue(fm.diet_flags?.value);

  // “method” in your current app is used for filtering (crockpot/instantpot/sheet-pan…)
  // Best mapping: use first appliance value as method (or "" if none)
  const method = appliance[0] || "";

  // ingredients/instructions stored as JSON text fields
  const ingredientsObjs = safeJsonParse(fm.ingredients_json?.value || "[]", []);
  const ingredients = Array.isArray(ingredientsObjs)
    ? ingredientsObjs.map(obj => {
        if (!obj) return "";
        if (typeof obj === "string") return obj;
        const qty = (obj.qty ?? "").toString().trim();
        const unit = (obj.unit ?? "").toString().trim();
        const name = (obj.name ?? "").toString().trim();
        return [qty, unit, name].filter(Boolean).join(" ").trim() || name || "";
      }).filter(Boolean)
    : [];

  const instructionsArr = safeJsonParse(fm.instructions_json?.value || "[]", []);
  const instructions = Array.isArray(instructionsArr)
    ? instructionsArr.map(String).filter(Boolean)
    : [];

  const notes = String(fm.notes?.value || "");

  // image (Storefront metaobject field may provide a reference)
  // We query for reference image below; if missing, blank
  const image = fm.image?.reference?.image?.url || fm.image?.reference?.url || "";

  return {
    id: slug,                 // keep stable for localStorage keys
    title,
    image,
    method,
    minutes: totalMin,
    tags: [...tags, ...dietFlags], // optional: include diet flags in tags for filtering
    ingredients,
    instructions,
    notes,
    servingsDefault: servings,     // optional extra if you want later
  };
}

async function fetchRecipesFromMetaobjects() {
  const query = `
    query GetRecipes($first: Int!) {
      metaobjects(type: "recipe", first: $first, sortKey: "updated_at") {
        nodes {
          id
          handle
          fields {
            key
            value
            reference {
              __typename
              ... on MediaImage {
                image { url altText }
              }
              ... on GenericFile {
                url
              }
            }
          }
        }
      }
    }
  `;

  const data = await storefrontGraphQL(query, { first: 250 });
  const nodes = data?.metaobjects?.nodes || [];
  return nodes
    .map(metaobjectToBaseRecipe)
    .filter(r => r.id && r.title);
}





  // -------- app state --------
  const App = {
    baseRecipes: [],   // from recipes.json
    customRecipes: [], // user-added
    myIds: new Set(),
    ratings: {},       // { [id]: number }
    servings: {}, // per-recipe selected servings
    selection: new Set(),
    // filters per page
    filters: {
      all: { ingredientQuery: "", method: "", timeBucket: "", minRating: 0, tags: new Set(), sort: "name_asc", filtersOpen: false },
      my:  { ingredientQuery: "", method: "", timeBucket: "", minRating: 0, tags: new Set(), sort: "name_asc", filtersOpen: false },
    },
    plan: { // day -> [ids]
      Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: []
    },
    listChecks: {}, // { "Produce|Bananas": true }
  };

  function allRecipesCombined() {
    // base + custom (custom are also eligible for viewing)
    const map = new Map();
    App.baseRecipes.forEach(r => map.set(r.id, r));
    App.customRecipes.forEach(r => map.set(r.id, r));
    return Array.from(map.values());
  }

  function getRecipeById(id) {
    return allRecipesCombined().find(r => r.id === id) || null;
  }

  function isInMyRecipes(id) {
    return App.myIds.has(id);
  }

  function setRating(id, stars) {
    App.ratings[id] = stars;
    saveLS(STORAGE.ratings, App.ratings);
  }

  function getRating(id) {
    const n = Number(App.ratings[id] || 0);
    return Number.isFinite(n) ? n : 0;
  }

  function persistMyIds() {
    saveLS(STORAGE.myIds, Array.from(App.myIds));
  }

  function persistCustom() {
    saveLS(STORAGE.custom, App.customRecipes);
  }

  function persistPlan() {
    saveLS(STORAGE.plan, App.plan);
  }

  function persistListChecks() {
    saveLS(STORAGE.listChecks, App.listChecks);
  }

  function persistRatings() {
    saveLS(STORAGE.ratings, App.ratings || {});
  }

  function getUserRating(recipeId) {
    const n = Number(App.ratings && App.ratings[recipeId]);
    return Number.isFinite(n) ? Math.max(0, Math.min(5, n)) : 0;
  }

  function setUserRating(recipeId, value) {
    const v = Math.max(0, Math.min(5, Number(value) || 0));
    if (!App.ratings) App.ratings = {};
    App.ratings[recipeId] = v;
    persistRatings();
  }

  function renderStarRating(container, value, opts = {}) {
    const { interactive = false, onChange = null } = opts;

    if (!container) return;

    const current = Math.max(0, Math.min(5, Number(value) || 0));
    container.innerHTML = "";
    container.classList.toggle("rb-stars--interactive", !!interactive);

    for (let i = 1; i <= 5; i++) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "rb-star";
      btn.setAttribute("aria-label", `${i} star${i === 1 ? "" : "s"}`);
      btn.setAttribute("aria-pressed", i <= current ? "true" : "false");
      btn.textContent = i <= current ? "★" : "☆";

      if (interactive) {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (typeof onChange === "function") onChange(i);
        });
        btn.addEventListener("touchstart", (e) => e.stopPropagation(), { passive: true });
      } else {
        btn.disabled = true;
      }

      container.appendChild(btn);
    }
  }


  function isInMealPlan(id) {
    return Object.values(App.plan).some(arr => (arr || []).includes(id));
  }

  function removeFromMealPlanEverywhere(id) {
    Object.keys(App.plan).forEach(day => {
      App.plan[day] = (App.plan[day] || []).filter(x => x !== id);
    });
    persistPlan();
  }

  function toggleMealPlan(id, day = "Mon") {
    if (isInMealPlan(id)) {
      removeFromMealPlanEverywhere(id);
      return false; // now removed
    }
    App.plan[day] = [...(App.plan[day] || []), id];
    persistPlan();
    return true; // now added
  }

  function pruneListChecksToCurrentPlan() {
    // Build a set of keys that currently exist from the meal plan
    const ids = Object.values(App.plan).flat().filter(Boolean);
    const recipes = ids.map(getRecipeById).filter(Boolean);

    const keep = new Set();
    recipes.forEach(r => {
      (r.ingredients || []).forEach(line => {
        const area = categorizeIngredient(line);
        keep.add(`${area}|${line}`);
      });
    });

    // Remove checks that no longer apply
    let changed = false;
    Object.keys(App.listChecks || {}).forEach(key => {
      if (!keep.has(key)) {
        delete App.listChecks[key];
        changed = true;
      }
    });

    if (changed) persistListChecks();
  }


  // -------- filtering/sorting --------
  function recipeMatches(r, f) {
    // method
    if (f.method && r.method !== f.method) return false;

    // time bucket
    if (f.timeBucket) {
      const max = Number(f.timeBucket);
      if (Number.isFinite(max) && max !== 999 && Number(r.minutes || 0) > max) return false;
    }

    // min rating
    if (Number(f.minRating || 0) > 0 && getRating(r.id) < Number(f.minRating)) return false;

    // tags: require all selected tags
    if (f.tags && f.tags.size) {
      const tags = new Set((r.tags || []).map(String));
      for (const t of f.tags) if (!tags.has(t)) return false;
    }

    // ingredient query
    const q = (f.ingredientQuery || "").trim().toLowerCase();
    if (q) {
      const needles = q.split(",").map(s => s.trim()).filter(Boolean);
      const hay = (r.ingredients || []).join(" | ").toLowerCase();
      for (const n of needles) if (!hay.includes(n)) return false;
    }

    return true;
  }

  function sortRecipes(list, sortKey) {
    const arr = [...list];
    const byName = (a, b) => a.title.localeCompare(b.title);
    const byTime = (a, b) => (Number(a.minutes || 0) - Number(b.minutes || 0));
    const byRating = (a, b) => (getRating(b.id) - getRating(a.id));

    switch (sortKey) {
      case "name_desc": return arr.sort((a, b) => -byName(a, b));
      case "time_asc":  return arr.sort(byTime);
      case "time_desc": return arr.sort((a, b) => -byTime(a, b));
      case "rating_desc": return arr.sort(byRating);
      case "name_asc":
      default: return arr.sort(byName);
    }
  }

  // -------- rendering building blocks --------
  function renderStars(n) {
    const full = "★".repeat(Math.max(0, Math.min(5, n)));
    const empty = "☆".repeat(5 - Math.max(0, Math.min(5, n)));
    return `<span aria-label="${n} out of 5 stars">${full}${empty}</span>`;
  }

  function mount(fragment) {
    const host = getHost();
    if (!host) return;
    host.innerHTML = "";
    host.appendChild(fragment);
    setActiveTab();
  }

  function wireTopTabs() {
    const root = getRoot();
    if (!root) return;
    qsa(".tab", root).forEach(btn => {
      btn.addEventListener("click", () => {
        const r = btn.getAttribute("data-route");
        if (r) location.hash = r;
      });
    });
  }

  function renderPageShell({ title, actionsNode }) {
    const frag = cloneTpl("tpl-page");
    const page = frag.querySelector(".page");
//    page.querySelector(".page-title").textContent = title;

    const actions = page.querySelector(".page-actions");
    if (actionsNode) actions.appendChild(actionsNode);

    return { frag, page };
  }

  function bindFilters(pageKey, pageEl) {
    const f = App.filters[pageKey];

    const filtersWrap = pageEl.querySelector('[data-bind="filters"]');

    // open/close filters
    const toggleBtn = pageEl.querySelector('[data-action="toggleFilters"]');
    toggleBtn.addEventListener("click", () => {
      f.filtersOpen = !f.filtersOpen;
      filtersWrap.style.display = f.filtersOpen ? "block" : "none";

    });
    filtersWrap.style.display = f.filtersOpen ? "block" : "none";


    // sort
    const sortSel = pageEl.querySelector('[data-bind="sort"]');
    sortSel.value = f.sort;
    sortSel.addEventListener("change", () => {
      f.sort = sortSel.value;
      render();
    });

    // ingredient query (apply on Enter to avoid focus loss)
    const iq = pageEl.querySelector('[data-bind="ingredientQuery"]');
    iq.value = f.ingredientQuery;

    iq.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();               // prevents form submits / accidental blur
        f.ingredientQuery = iq.value;
        render();
      }
    });
    //clear ingredient filter
    iq.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { iq.value = ""; f.ingredientQuery = ""; render(); }
    });


    // method
    const methodSel = pageEl.querySelector('[data-bind="method"]');
    methodSel.value = f.method;
    methodSel.addEventListener("change", () => {
      f.method = methodSel.value;
      render();
    });

    // time
    const timeSel = pageEl.querySelector('[data-bind="timeBucket"]');
    timeSel.value = f.timeBucket;
    timeSel.addEventListener("change", () => {
      f.timeBucket = timeSel.value;
      render();
    });

    // rating
    const ratingSel = pageEl.querySelector('[data-bind="minRating"]');
    ratingSel.value = String(f.minRating || 0);
    ratingSel.addEventListener("change", () => {
      f.minRating = Number(ratingSel.value || 0);
      render();
    });

    // tags chips
    qsa(".chip input[type=checkbox]", filtersWrap).forEach(chk => {
      const tag = chk.getAttribute("data-tag") || "";
      chk.checked = f.tags.has(tag);
      chk.addEventListener("change", () => {
        if (chk.checked) f.tags.add(tag);
        else f.tags.delete(tag);
        render();
      });
    });
  }

  const SERVING_OPTIONS = [2, 4, 6, 8];

  function getDefaultServings(recipe) {
    const s = Number(recipe && recipe.servings);
    return SERVING_OPTIONS.includes(s) ? s : 4; // default base servings
  }

  function getSelectedServings(recipe) {
    const def = getDefaultServings(recipe);
    const s = Number(App.servings && App.servings[recipe.id]);
    return SERVING_OPTIONS.includes(s) ? s : def;
  }

  function setSelectedServings(recipe, servings) {
    const def = getDefaultServings(recipe);
    const val = Number(servings);
    App.servings[recipe.id] = SERVING_OPTIONS.includes(val) ? val : def;
    saveLS(STORAGE.servings, App.servings);

    // Shopping list checkbox keys will change when quantities change,
    // so clear checks to avoid mismatched “checked” items.
    App.listChecks = {};
    persistListChecks();
  }

  function servingsFactor(recipe) {
    const def = getDefaultServings(recipe);
    const sel = getSelectedServings(recipe);
    return def ? (sel / def) : 1;
  }

  function scaleParsedIngredient(parsed, factor) {
    return {
      ...parsed,
      min: parsed.min * factor,
      max: parsed.max * factor,
    };
  }

  function renderScaledIngredientLine(line, factor) {
    const parsed = _rbParseIngredient(line);
    if (parsed && parsed.type === "parsed") {
      const scaled = scaleParsedIngredient(parsed, factor);
      return _rbDisplayParsed(scaled);
    }
    return _rbCleanLine(line);
  }


  function buildTile(recipe, context /* "all" | "my" | "plan" */) {
    const frag = cloneTpl("tpl-tile");
    const tile = frag.querySelector(".tile");

    // image
    const img = tile.querySelector('[data-bind="img"]');
    if (img) {
      // Always reset from fallback state first
      img.textContent = "";
      img.style.display = "";
      img.style.placeItems = "";
      img.style.fontSize = "";

      if (recipe.image) {
        img.style.backgroundImage = `url("${recipe.image}")`;
        img.style.backgroundSize = "cover";
        img.style.backgroundPosition = "center";
        img.style.backgroundRepeat = "no-repeat";
        img.setAttribute("aria-label", recipe.title || "Recipe image");
      } else {
        img.style.backgroundImage = "";
        img.textContent = "🍲";
        img.style.display = "grid";
        img.style.placeItems = "center";
        img.style.fontSize = "28px";
        img.setAttribute("aria-label", recipe.title || "Recipe");
      }
    }

    // title
    tile.querySelector('[data-bind="title"]').textContent = recipe.title;

    // meta
    const method = tile.querySelector('[data-bind="method"]');
    method.textContent = recipe.method ? recipe.method.replace(/_/g, " ") : "";

    const time = tile.querySelector('[data-bind="time"]');
    time.textContent = recipe.minutes ? `${recipe.minutes} min` : "";

    const tags = tile.querySelector('[data-bind="tags"]');
    tags.textContent = (recipe.tags || []).slice(0, 2).join(", ");

    // clickable stars
    const starsEl = tile.querySelector('[data-bind="stars"]');
    if (starsEl) {
      const val = getUserRating(recipe.id);
      renderStarRating(starsEl, val, {
        interactive: true,
        onChange: (n) => {
          setUserRating(recipe.id, n);
          // update tile immediately
          renderStarRating(starsEl, n, {
            interactive: true,
            onChange: (m) => {
              setUserRating(recipe.id, m);
              render(); // keep everything in sync if you filter by rating later
            }
          });
          render(); // refresh other views that show this rating
        }
      });
    }


    // Servings dropdown on tile
    const servingsSel = tile.querySelector('[data-bind="servings"]');
    if (servingsSel) {
      servingsSel.value = String(getSelectedServings(recipe));
      servingsSel.addEventListener("click", (e) => e.stopPropagation());
      servingsSel.addEventListener("change", (e) => {
        e.stopPropagation();
        setSelectedServings(recipe, servingsSel.value);
        // Update UI everywhere (tiles + detail + shopping list)
        render();
      });
    }


    // Buttons: toggle My Recipes + Meal Plan (added / not added)
    const myBtn = tile.querySelector('[data-bind="myBtn"]');
    const planBtn = tile.querySelector('[data-bind="planBtn"]');

    function refreshActionButtons() {
      const inMy = isInMyRecipes(recipe.id);
      const inPlan = isInMealPlan(recipe.id);

      if (myBtn) myBtn.textContent = inMy ? "✓ My Recipes" : "+ My Recipes";
      if (planBtn) planBtn.textContent = inPlan ? "✓ Meal Plan" : "+ Meal Plan";
    }

    if (myBtn) {
      myBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isInMyRecipes(recipe.id)) App.myIds.delete(recipe.id);
        else App.myIds.add(recipe.id);

        persistMyIds();
        refreshActionButtons();

        // if you're on My Recipes page, removing should update the grid immediately
        render();
      });
    }

    if (planBtn) {
      planBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        // toggle add/remove in meal plan (added anywhere counts as "added")
        toggleMealPlan(recipe.id, "Mon");

        refreshActionButtons();
      });
    }

    refreshActionButtons();

    // open detail
    tile.addEventListener("click", () => {
      location.hash = `#/r/${encodeURIComponent(recipe.id)}`;
    });

    return frag;
  }

  function renderGrid(pageEl, recipes, pageKey) {
    const grid = pageEl.querySelector('[data-bind="grid"]');
    const empty = pageEl.querySelector('[data-bind="emptyState"]');

    grid.innerHTML = "";
    if (!recipes.length) {
      empty.style.display = "";
      return;
    }
    empty.style.display = "none";

    recipes.forEach(r => grid.appendChild(buildTile(r, pageKey)));
  }

  // -------- pages --------
  function renderAll() {
    const { frag, page } = renderPageShell({});
    bindFilters("all", page);

    const f = App.filters.all;
    const list = sortRecipes(
      allRecipesCombined().filter(r => recipeMatches(r, f)),
      f.sort
    );

    renderGrid(page, list, "all");

    mount(frag);
  }


  function renderMy() {
    const { frag, page } = renderPageShell({});

    bindFilters("my", page);

    const f = App.filters.my;
    const list = sortRecipes(
      allRecipesCombined()
        .filter(r => App.myIds.has(r.id))
        .filter(r => recipeMatches(r, f)),
      f.sort
    );

    renderGrid(page, list, "my");

    mount(frag);

    // ---- Bottom bar "Add Recipe" button (only on My Recipes) ----
    const root = getRoot();
    if (!root) return;

    const bar = root.querySelector("#recipe-box-bottom-bar");
    if (!bar) return;

    // Clear any previous button/content in the bottom bar
    bar.innerHTML = "";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "rb-bottom-btn";
    btn.setAttribute("aria-label", "Add a new recipe");
    btn.innerHTML = `<span class="rb-bottom-btn__icon">＋</span><span>Add Recipe</span>`;

    btn.addEventListener("click", () => (location.hash = "#/add"));

    bar.appendChild(btn);
  }

  function prettyMethod(method) {
    const m = String(method || "").toLowerCase();
    if (m === "crockpot") return "Crockpot";
    if (m === "instant_pot") return "Instant Pot";
    if (m === "stovetop") return "Stove top / Skillet";
    if (m === "roasting") return "Roasting / Oven";
    if (m === "no_cook") return "No-cook";
    return method || "";
  }


  function renderDetail(id) {
    const r = getRecipeById(id);
    if (!r) {
      showMessageCard(`<b>Recipe not found.</b><div class="muted">ID: ${escapeHtml(id)}</div>`);
      return;
    }

    // Create the detail page DOM
    const frag = cloneTpl("tpl-detail");
    const pageEl = frag.querySelector(".detail");
    if (!pageEl) {
      showMessageCard(`<b>Template error:</b><div class="muted">tpl-detail not found.</div>`);
      return;
    }

    // Back button
    const backBtn = pageEl.querySelector('[data-action="back"]');
    if (backBtn) backBtn.addEventListener("click", () => history.back());

    // Save toggle button (if you have it)
    const saveBtn = pageEl.querySelector('[data-action="toggleSave"]');
    if (saveBtn) {
      const refreshSave = () => {
        saveBtn.textContent = isInMyRecipes(r.id) ? "Remove from My Recipes" : "Add to My Recipes";
      };
      refreshSave();
      saveBtn.addEventListener("click", () => {
        if (isInMyRecipes(r.id)) App.myIds.delete(r.id);
        else App.myIds.add(r.id);
        persistMyIds();
        refreshSave();
      });
    }

    // Servings factor
    const factor = servingsFactor(r);

    // Bind servings dropdown (DETAIL)
    const servingsSel = pageEl.querySelector('[data-bind="servings"]');
    if (servingsSel) {
      servingsSel.value = String(getSelectedServings(r));
      servingsSel.addEventListener("change", () => {
        setSelectedServings(r, servingsSel.value);
        render(); // re-render detail with new factor + shopping list impact
      });
    }

    // Fill content
    const imgEl = pageEl.querySelector('[data-bind="img"]');
    if (imgEl) imgEl.style.backgroundImage = r.image ? `url("${r.image}")` : "";

    const titleEl = pageEl.querySelector('[data-bind="title"]');
    if (titleEl) titleEl.textContent = r.title || "";

    const metaEl = pageEl.querySelector('[data-bind="meta"]');
    if (metaEl) metaEl.textContent = `${prettyMethod(r.method)} • ${r.minutes || 0} min • ${getSelectedServings(r)} servings`;

    const tagsEl = pageEl.querySelector('[data-bind="tags"]');
    if (tagsEl) tagsEl.textContent = (r.tags || []).join(" • ");

    const ratingEl = pageEl.querySelector('[data-bind="ratingStars"]');
    if (ratingEl) {
      renderStarRating(ratingEl, getUserRating(r.id), {
        interactive: true,
        onChange: (n) => {
          setUserRating(r.id, n);
          render(); // re-render detail + tiles if visible
        }
      });
    }


    // Ingredients (scaled)
    const ingEl = pageEl.querySelector('[data-bind="ingredients"]');
    if (ingEl) {
      ingEl.innerHTML = (r.ingredients || [])
        .map(line => `<li>${escapeHtml(renderScaledIngredientLine(line, factor))}</li>`)
        .join("");
    }

    // Instructions
    const insEl = pageEl.querySelector('[data-bind="instructions"]');
    if (insEl) {
      insEl.innerHTML = (r.instructions || [])
        .map(step => `<li>${escapeHtml(step)}</li>`)
        .join("");
    }

    // Notes
    const notesEl = pageEl.querySelector('[data-bind="notes"]');
    if (notesEl) notesEl.textContent = r.notes || "";

    mount(frag);
  }


  function newId() {
    return "u_" + Math.random().toString(36).slice(2, 9) + "_" + Date.now().toString(36);
  }

  function renderAdd() {
    const frag = cloneTpl("tpl-add");
    const form = frag.querySelector(".form");

    const back = qs('[data-action="back"]', form);
    back.addEventListener("click", () => history.back());

    const getVal = (bind) => qs(`[data-bind="${bind}"]`, form)?.value ?? "";
    const setVal = (bind, val) => { const el = qs(`[data-bind="${bind}"]`, form); if (el) el.value = val; };

    qs('[data-action="pasteParse"]', form).addEventListener("click", () => {
      // very lightweight parser: if user pasted a big blob into ingredients or instructions, split lines
      const ing = getVal("ingredients");
      const ins = getVal("instructions");
      if (ing && !ing.includes("\n")) setVal("ingredients", ing.split(/[,;]/).map(s => s.trim()).filter(Boolean).join("\n"));
      if (ins && !ins.includes("\n")) setVal("instructions", ins.split(/[\.\n]/).map(s => s.trim()).filter(Boolean).join("\n"));
    });

    qs('[data-action="save"]', form).addEventListener("click", () => {
      const title = getVal("title").trim();
      if (!title) {
        alert("Please add a title.");
        return;
      }

      const method = getVal("method");
      const minutes = Number(getVal("minutes") || 0);
      const tags = getVal("tags").split(",").map(s => s.trim()).filter(Boolean);
      const image = getVal("image").trim();

      const ingredients = getVal("ingredients").split("\n").map(s => s.trim()).filter(Boolean);
      const instructions = getVal("instructions").split("\n").map(s => s.trim()).filter(Boolean);
      const notes = getVal("notes").trim();

      const id = newId();
      const servings = Number(getVal("servings") || 4);

      const recipe = { id, title, method, minutes, tags, image, servings, ingredients, instructions, notes };


      App.customRecipes.unshift(recipe);
      persistCustom();

      // auto-add to My Recipes
      App.myIds.add(id);
      setSelectedServings(recipe, servings);
      persistMyIds();

      location.hash = "#/my";
    });

    mount(frag);
  }

  function renderPlan() {
    const frag = cloneTpl("tpl-plan");
    const root = frag.querySelector(".plan");

    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    // Buttons
    qs('[data-action="clearPlan"]', root).addEventListener("click", () => {
      App.plan = { Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: [] };
      persistPlan();
      render();
    });

    qs('[data-action="buildList"]', root).addEventListener("click", () => {
      location.hash = "#/list";
    });

    const grid = qs('[data-bind="planGrid"]', root);
    grid.innerHTML = "";

    const isTouch =
      "ontouchstart" in window ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);

    window.__RB_DRAG_ID = window.__RB_DRAG_ID || "";

    const allowDrop = (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    };

    const readDragId = (e) => {
      const dt = e.dataTransfer;
      const fromDT = dt
        ? (dt.getData("text/plain") ||
          dt.getData("text") ||
          dt.getData("Text") ||
          "")
        : "";
      return (fromDT || window.__RB_DRAG_ID || "").trim();
    };

    const removeFromDay = (id, day) => {
      if (!id) return;
      App.plan[day] = (App.plan[day] || []).filter(x => x !== id);
      persistPlan();
      pruneListChecksToCurrentPlan();
      render();
    };


    const moveToDay = (id, day) => {
      if (!id) return;

      days.forEach(d => {
        App.plan[d] = (App.plan[d] || []).filter(x => x !== id);
      });

      App.plan[day] = [...(App.plan[day] || []), id];

      persistPlan();
      pruneListChecksToCurrentPlan();
      render();
    };

    days.forEach(day => {
      const col = document.createElement("div");
      col.className = "card";
      col.innerHTML = `
        <h2 style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
          <span>${day}</span>
          ${isTouch
            ? `<span class="muted" style="font-size:12px;">Tap “Move”</span>`
            : `<span class="muted" style="font-size:12px;">Drag here</span>`}
        </h2>
        <div class="plan-drop" data-day="${day}" style="min-height:48px;"></div>
      `;

      const drop = qs(".plan-drop", col);

      if (!isTouch) {
        drop.addEventListener("dragenter", allowDrop, true);
        drop.addEventListener("dragover", allowDrop, true);
        drop.addEventListener("drop", (e) => {
          e.preventDefault();
          const id = readDragId(e);
          moveToDay(id, day);
        }, true);

        col.addEventListener("dragenter", allowDrop, true);
        col.addEventListener("dragover", allowDrop, true);
        col.addEventListener("drop", (e) => {
          e.preventDefault();
          const id = readDragId(e);
          moveToDay(id, day);
        }, true);
      }

      // Render tiles for this day
      const ids = App.plan[day] || [];
      ids.forEach(id => {
        const r = getRecipeById(id);
        if (!r) return;

        const tileFrag = buildTile(r, "plan");
        const tile = tileFrag.querySelector(".tile");

        // Hide checkbox + save button on meal plan tiles
        const cb = tile.querySelector(".checkbox");
        if (cb) cb.style.display = "none";
        const saveBtn = tile.querySelector('[data-bind="saveBtn"]');
        if (saveBtn) saveBtn.style.display = "none";

        // ---------- Remove button ----------
        // Put it in the top-right of the tile body
        const body = tile.querySelector(".tile-body") || tile;
        const removeBtn = document.createElement("button");
        removeBtn.type = "button";
        removeBtn.className = "rb-remove";
        removeBtn.setAttribute("aria-label", `Remove ${r.title} from ${day}`);
        removeBtn.textContent = "✕";

        // Don’t open recipe when tapping remove
        removeBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          removeFromDay(r.id, day);
        });
        removeBtn.addEventListener("touchstart", (e) => e.stopPropagation());

        body.appendChild(removeBtn);

        // ---------- Touch fallback: Move dropdown ----------
        if (isTouch) {
          const moveWrap = document.createElement("div");
          moveWrap.style.marginLeft = "auto";

          moveWrap.innerHTML = `
            <label class="label" style="font-size:12px;margin:0 0 4px 0;">Move</label>
            <select class="select" data-move="${r.id}" style="min-width:140px;">
              ${days.map(d => `<option value="${d}" ${d === day ? "selected" : ""}>${d}</option>`).join("")}
            </select>
          `;

          moveWrap.addEventListener("click", (e) => e.stopPropagation());
          moveWrap.addEventListener("touchstart", (e) => e.stopPropagation());

          const sel = moveWrap.querySelector("select");
          sel.addEventListener("change", () => moveToDay(r.id, sel.value));

          const foot = tile.querySelector(".tile-foot");
          if (foot) foot.appendChild(moveWrap);
          else tile.appendChild(moveWrap);

        } else {
          // Desktop drag/drop
          tile.removeAttribute("draggable"); // template sets draggable="false"
          tile.draggable = true;
          tile.setAttribute("draggable", "true");

          tile.addEventListener("dragstart", (e) => {
            window.__RB_DRAG_ID = r.id;
            const dt = e.dataTransfer;
            if (!dt) return;

            dt.effectAllowed = "move";
            dt.setData("text/plain", r.id);
            dt.setData("text", r.id);
            dt.setData("Text", r.id);

            try { dt.setDragImage(tile, 20, 20); } catch (_) {}
          });
        }

        drop.appendChild(tileFrag);
      });

      grid.appendChild(col);
    });

    mount(frag);
  }




  function categorizeIngredient(line) {
    const s = line.toLowerCase();

    const has = (...words) => words.some(w => s.includes(w));

    if (has("milk","cheese","yogurt","butter","cream","sour cream","half and half")) return "Dairy";
    if (has("chicken","turkey","beef","pork","ham","sausage","fish","salmon","shrimp","bacon")) return "Meat & Seafood";
    if (has("apple","banana","berries","strawberry","blueberry","broccoli","spinach","lettuce","tomato","onion","garlic","pepper","carrot","potato","sweet potato","avocado","zucchini","cucumber","mushroom")) return "Produce";
    if (has("bread","bun","tortilla","wrap","bagel")) return "Bakery";
    if (has("frozen","peas","corn","mixed veggies")) return "Frozen";
    if (has("rice","pasta","noodles","beans","lentil","canned","tomato sauce","broth","stock","oil","vinegar","salsa","taco","soy sauce","coconut milk")) return "Pantry";
    if (has("salt","pepper","paprika","cumin","garlic powder","onion powder","oregano","basil","curry","garam","cinnamon")) return "Spices";
    return "Other";
  }

  function _rbCleanLine(line) {
  return String(line || "")
    .replace(/\u2013|\u2014/g, "-")     // en/em dash → hyphen
    .replace(/\s+/g, " ")
    .trim();
}

function _rbParseNumber(str) {
  str = String(str || "").trim();
  if (!str) return null;

  // fraction like 1/2
  if (/^\d+\/\d+$/.test(str)) {
    const [a, b] = str.split("/").map(Number);
    if (b) return a / b;
  }

  const n = Number(str);
  return Number.isFinite(n) ? n : null;
}

function _rbFormatQty(n) {
  // Helper: best rational approximation with bounded denominator
  function approximateFraction(x, maxDen = 8) {
    if (!Number.isFinite(x)) return null;
    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);
    let best = { n: 0, d: 1, err: Infinity };
    for (let d = 1; d <= maxDen; d++) {
      const nn = Math.round(x * d);
      const err = Math.abs(x - nn / d);
      if (err < best.err) best = { n: nn, d, err };
    }
    if (best.d === 0) return null;
    // reduce
    const gcd = (a, b) => b ? gcd(b, a % b) : a;
    const g = gcd(best.n, best.d) || 1;
    return { num: best.n / g, den: best.d / g, err: best.err, sign };
  }

  if (!Number.isFinite(n)) return "";
  // keep a modest precision baseline
  const val = Math.round(n * 100) / 100;
  const sign = val < 0 ? "-" : "";
  const absVal = Math.abs(val);
  const whole = Math.floor(absVal);
  const fracPart = +(absVal - whole).toFixed(6);

  // If effectively an integer
  if (fracPart < 1e-6) return sign + String(whole);

  const frac = approximateFraction(fracPart, 8);
  // if approximation is poor, fall back to trimmed decimal (max 2 dp)
  if (!frac || frac.err > 0.02) {
    const rounded = Math.round(val * 100) / 100;
    if (Math.abs(rounded - Math.round(rounded)) < 0.00001) return String(Math.round(rounded));
    return String(rounded).replace(/(\.\d*?[1-9])0+$/,"$1").replace(/\.0+$/,"");
  }

  // handle cases like 4/4 -> increment whole
  if (frac.num === frac.den) return sign + String(whole + 1);

  const fracStr = `${frac.num}/${frac.den}`;
  if (whole === 0) return sign + fracStr;
  return sign + `${whole} ${fracStr}`;
}

function _rbNormalizeItemName(name) {
  return _rbCleanLine(name)
    .replace(/\(.*?\)/g, "")           // remove parentheses
    .split(",")[0]                      // remove trailing notes after comma
    .replace(/\b(optional|to taste)\b/gi, "")
    .trim()
    .toLowerCase();
}

  // unit mapping (display + canonical key)
  const _RB_UNITS = {
    tsp: { key: "tsp", display: "tsp" },
    tbsp: { key: "tbsp", display: "tbsp" },
    cup: { key: "cup", display: "cup" },
    cups: { key: "cup", display: "cup" },
    ounce: { key: "oz", display: "oz" },
    ounces: { key: "oz", display: "oz" },
    oz: { key: "oz", display: "oz" },
    pound: { key: "lb", display: "lb" },
    pounds: { key: "lb", display: "lb" },
    lb: { key: "lb", display: "lb" },
    lbs: { key: "lb", display: "lb" },
    can: { key: "can", display: "can" },
    cans: { key: "can", display: "can" },
    pkg: { key: "pkg", display: "pkg" },
    package: { key: "pkg", display: "pkg" },
    packages: { key: "pkg", display: "pkg" },
    clove: { key: "clove", display: "clove" },
    cloves: { key: "clove", display: "clove" },
    egg: { key: "egg", display: "egg" },
    eggs: { key: "egg", display: "egg" }
  };

  function _rbParseIngredient(line) {
    const raw = _rbCleanLine(line);
    if (!raw) return null;

    // Don’t try to combine these
    if (/to taste/i.test(raw)) return { type: "raw", raw };
    if (/^salt\b/i.test(raw) || /^pepper\b/i.test(raw) || /^salt\/pepper\b/i.test(raw)) return { type: "raw", raw };

    // Match: qty [range] + unit + rest
    // Examples: "1 lb ground turkey", "1/2 cup milk", "2-3 tbsp olive oil", "1 can beans"
    const m = raw.match(/^(\d+(?:\.\d+)?|\d+\/\d+)\s*(?:[-–]\s*(\d+(?:\.\d+)?|\d+\/\d+))?\s+([A-Za-z]+)\b\s*(.*)$/);
    if (!m) return { type: "raw", raw };

    const q1 = _rbParseNumber(m[1]);
    const q2 = _rbParseNumber(m[2]);
    const unitToken = (m[3] || "").toLowerCase();
    const rest = _rbCleanLine(m[4] || "");

    if (!Number.isFinite(q1)) return { type: "raw", raw };

    const unitInfo = _RB_UNITS[unitToken];
    if (!unitInfo) return { type: "raw", raw }; // unknown unit → don’t combine

    const min = q1;
    const max = Number.isFinite(q2) ? q2 : q1;

    const nameNorm = _rbNormalizeItemName(rest);
    if (!nameNorm) return { type: "raw", raw };

    return {
      type: "parsed",
      min, max,
      unitKey: unitInfo.key,
      unitDisplay: unitInfo.display,
      name: rest,
      nameNorm
    };
  }

  function _rbDisplayParsed(p) {
    const qty = (p.min === p.max)
      ? _rbFormatQty(p.min)
      : `${_rbFormatQty(p.min)}–${_rbFormatQty(p.max)}`;

    // simple pluralization for a few human units
    let unit = p.unitDisplay;
    const pluralize = (p.max > 1.00001);
    if (p.unitKey === "can") unit = pluralize ? "cans" : "can";
    if (p.unitKey === "pkg") unit = pluralize ? "pkgs" : "pkg";
    if (p.unitKey === "clove") unit = pluralize ? "cloves" : "clove";
    if (p.unitKey === "egg") unit = pluralize ? "eggs" : "egg";
    if (p.unitKey === "cup") unit = pluralize ? "cups" : "cup";
    if (p.unitKey === "lb") unit = pluralize ? "lbs" : "lb";

    return `${qty} ${unit} ${p.name}`.trim();
  }


  function renderList() {
    const frag = cloneTpl("tpl-list");
    const root = frag.querySelector(".listpage");

    qs('[data-action="clearChecks"]', root).addEventListener("click", () => {
      App.listChecks = {};
      persistListChecks();
      render();
    });

    const areas = qs('[data-bind="areas"]', root);
    const empty = qs('[data-bind="emptyState"]', root);
    areas.innerHTML = "";

    // gather recipe ids from plan
    const ids = Object.values(App.plan).flat().filter(Boolean);
    const recipes = ids.map(getRecipeById).filter(Boolean);

    // Build aggregated items by area
    // area -> key -> { display, checkedKey }
    const grouped = new Map();

    function ensureArea(area) {
      if (!grouped.has(area)) grouped.set(area, new Map());
      return grouped.get(area);
    }

    recipes.forEach(r => {
      const factor = servingsFactor(r);

      (r.ingredients || []).forEach(line => {
        const parsed = _rbParseIngredient(line);
        const rawLine = _rbCleanLine(line);
        if (!rawLine) return;

        const area = categorizeIngredient(rawLine);
        const areaMap = ensureArea(area);

        if (parsed && parsed.type === "parsed") {
          const scaled = scaleParsedIngredient(parsed, factor);
          const key = `${scaled.unitKey}|${scaled.nameNorm}`;

          if (!areaMap.has(key)) {
            areaMap.set(key, {
              type: "parsed",
              min: scaled.min,
              max: scaled.max,
              unitKey: scaled.unitKey,
              unitDisplay: scaled.unitDisplay,
              name: scaled.name,
              nameNorm: scaled.nameNorm
            });
          } else {
            const cur = areaMap.get(key);
            cur.min += scaled.min;
            cur.max += scaled.max;
          }
        } else {
          const key = `raw|${rawLine.toLowerCase()}`;
          if (!areaMap.has(key)) areaMap.set(key, { type: "raw", raw: rawLine });
        }
      });

    });

    // if nothing
    const hasAny = Array.from(grouped.values()).some(m => m.size > 0);
    if (!hasAny) {
      empty.style.display = "";
      mount(frag);
      return;
    }
    empty.style.display = "none";

    // Render areas
    Array.from(grouped.keys()).sort().forEach(areaName => {
      const itemsMap = grouped.get(areaName);

      const card = document.createElement("div");
      card.className = "card";

      // Convert to display strings
      const displayItems = Array.from(itemsMap.values()).map(obj => {
        if (obj.type === "parsed") return _rbDisplayParsed(obj);
        return obj.raw;
      }).sort((a, b) => a.localeCompare(b));

      // Build checklist UI
      const body = displayItems.map(text => {
        const key = `${areaName}|${text}`; // checklist key tied to display text
        const checked = !!App.listChecks[key];
        return `
          <label class="chip" style="display:flex;gap:10px;align-items:flex-start;">
            <input type="checkbox" data-check="${escapeHtml(key)}" ${checked ? "checked" : ""} />
            <span>${escapeHtml(text)}</span>
          </label>
        `;
      }).join("");

      card.innerHTML = `<h2>${escapeHtml(areaName)}</h2><div class="tags">${body}</div>`;
      areas.appendChild(card);
    });

    // Prune old checks that no longer exist (because list items changed/combined)
    const keepKeys = new Set();
    qsa('input[type="checkbox"][data-check]', areas).forEach(chk => {
      keepKeys.add(chk.getAttribute("data-check"));
    });
    let changed = false;
    Object.keys(App.listChecks || {}).forEach(k => {
      if (!keepKeys.has(k)) { delete App.listChecks[k]; changed = true; }
    });
    if (changed) persistListChecks();

    // bind checks
    qsa('input[type="checkbox"][data-check]', areas).forEach(chk => {
      chk.addEventListener("change", () => {
        const key = chk.getAttribute("data-check");
        if (!key) return;
        App.listChecks[key] = chk.checked;
        persistListChecks();
      });
    });

    mount(frag);
  }


  // -------- main render router --------
  function render() {
    wireTopTabs();

    const { page, id } = parseRoute();
    setActiveTab();

    // Remove FAB when not on My Recipes page
    const root = getRoot();
    if (root) {
      const bar = root.querySelector("#recipe-box-bottom-bar");
      if (bar && page !== "my") bar.innerHTML = "";
    }


    if (page === "all") return renderAll();
    if (page === "my") return renderMy();
    if (page === "plan") return renderPlan();
    if (page === "list") return renderList();
    if (page === "add") return renderAdd();
    if (page === "r") return renderDetail(decodeURIComponent(id || ""));

    // default
    location.hash = "#/all";
  }

  // -------- boot --------
  async function boot() {
    const root = getRoot();
    const host = getHost();

    if (!root || !host) return; // section not on this page

    // load user state
    App.myIds = new Set(loadLS(STORAGE.myIds, []));
    App.ratings = loadLS(STORAGE.ratings, {});
    App.customRecipes = loadLS(STORAGE.custom, []);
    App.plan = loadLS(STORAGE.plan, App.plan);
    App.listChecks = loadLS(STORAGE.listChecks, {});
    App.servings = loadLS(STORAGE.servings, {}); // { [recipeId]: 2|4|6|8 }


    try {
      if (USE_METAOBJECTS) {
        App.baseRecipes = await fetchRecipesFromMetaobjects();
      } else {
        const res = await fetch(DATA_URL, { cache: "no-store" });
        if (!res.ok) throw new Error(`recipes.json fetch failed (${res.status})`);
        const data = await res.json();

        const arr = Array.isArray(data) ? data : (data.recipes || []);
        App.baseRecipes = (arr || []).map(r => ({
          id: String(r.id),
          title: String(r.title || ""),
          image: r.image ? String(r.image) : "",
          method: String(r.method || ""),
          minutes: Number(r.minutes || 0),
          tags: Array.isArray(r.tags) ? r.tags.map(String) : [],
          ingredients: Array.isArray(r.ingredients) ? r.ingredients.map(String) : [],
          instructions: Array.isArray(r.instructions) ? r.instructions.map(String) : [],
          notes: String(r.notes || "")
        })).filter(r => r.id && r.title);
      }


      if (!App.baseRecipes.length && !App.customRecipes.length) {
        showMessageCard(
          `<b>No recipes loaded.</b>
           <div class="muted">I fetched <code>${escapeHtml(DATA_URL)}</code> but found 0 recipes.</div>
           <div class="muted">Check that your JSON contains <code>{"recipes":[...]}</code> or is a top-level array.</div>`
        );
        return;
      }

      window.addEventListener("hashchange", render);
      if (!location.hash) location.hash = "#/all";
      render();

    } catch (e) {
      showMessageCard(
        `<b>Recipe Box error</b>
         <div class="muted">${escapeHtml(String(e))}</div>
         <div class="muted">Data URL: <code>${escapeHtml(DATA_URL)}</code></div>`
      );
      // also log for devtools
      console.error(e);
    }
  }

  document.addEventListener("DOMContentLoaded", boot);
})();
